import * as React from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import styled, { DefaultTheme, withTheme } from "styled-components";

import {
  Analytics,
  FavouriteBtn,
  HelmetCanonical,
  SidebarBox,
  SvgSprite,
  VolunteerSearchLink,
  useInitialFocus,
  useReferrer,
} from "@skv/core";

import { locationSegmentsToReactNode, resizeImg } from "@skv/util";

import { organisationStore, useAppSettings } from "@skv/state";

import { ThemeProvider } from "styled-components";

import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import { useUserDispatch } from "~/state";
import { Error500, OrganisationDetailsOpportunityResultCard } from "~/ui";
import { OrganisationDetails404 } from "~/ui/ServerResponse";

const OppListSvgSprite = styled(SvgSprite)`
  vertical-align: text-top;
  margin: 0.15rem 0.35rem 0 0.35rem;
`;

const Block = styled(SidebarBox)`
  background-color: ${(p) => p.theme.gray200};
  border: none;
  padding: 1rem;
  margin-bottom: 1rem;
  p:last-child {
    margin-bottom: 0;
  }
`;

const OrganisationDetails: React.FC<{
  theme: DefaultTheme;
}> = ({ theme }) => {
  const { updateUser } = useUserDispatch();
  const wrapperRef = React.useRef(null);

  const { applicationUrl } = useAppSettings();

  const referrer = useReferrer();

  const { orgId } = useParams<{ orgId: string }>();

  const {
    loading,
    error,
    data: organisation,
  } = organisationStore.hooks.useOrganisation(orgId);

  const organisationHeaderRef = useInitialFocus<HTMLHeadingElement>({
    shouldFocus: !(loading || error || !organisation),
  });

  if (error) {
    switch (error.errorCode) {
      case "error:organisation:notfound":
        return <OrganisationDetails404 />;
      case "error:organisation":
      default:
        return <Error500 />;
    }
  }

  return (
    <div ref={wrapperRef} className="my-4">
      {((): React.ReactNode => {
        if (loading) {
          return <p className="mt-3">Loading please wait...</p>;
        }

        if (organisation) {
          return (
            <>
              <Helmet>
                <title>{`Volunteer at ${organisation.name}`}</title>
                <meta
                  name="description"
                  content={`${organisation.description}`}
                />
                <meta
                  property="og:title"
                  content={`Volunteer at ${organisation.name}`}
                />
                <meta
                  property="og:description"
                  content={`${organisation.description}`}
                />
              </Helmet>
              <HelmetCanonical
                url={`${applicationUrl}${organisation.canonicalUrl}`}
              />
              <Analytics
                data={{
                  subSection: "organisation",
                  subSubSection: "organisation:details",
                  subSubSubSection: "organisation:details",
                  pageTitle: "organisation:details",
                  pageType: "organisation:details",
                  organisationId: organisation.id,
                }}
              />
              <section>
                <header
                  css={`
                    display: flex;
                    margin: 0 -1rem;
                    flex-direction: column;

                    @media (min-width: ${(p): string => p.theme.mdBreakpoint}) {
                      align-items: center;
                      flex-direction: row;
                    }
                  `}
                >
                  <div
                    className="mb-3"
                    css={`
                      padding: 0 1rem;
                      flex: 1 1 auto;

                      order: 2;

                      @media (min-width: ${(p): string =>
                          p.theme.mdBreakpoint}) {
                        order: 1;
                      }
                    `}
                  >
                    <h1
                      ref={organisationHeaderRef}
                      className="display-2 font-weight-bold"
                      tabIndex={-1}
                    >
                      {organisation.name}
                    </h1>
                  </div>
                  {!!organisation.logoUrl && (
                    <div
                      className="col-lg-4 offset-lg-1 d-flex flex-column justify-content-md-start align-items-start gap-3"
                      css={`
                        padding: 0 1rem;
                        flex: 0 1 auto;
                        order: 1;
                      `}
                    >
                      <picture>
                        <source
                          media={`(min-width: ${theme.xlBreakpoint})`}
                          srcSet={[
                            `${resizeImg(organisation.logoUrl, "120x90")} 1x`,
                            `${resizeImg(organisation.logoUrl, "240x180")} 2x`,
                          ].join(", ")}
                        />
                        <source
                          srcSet={[
                            `${resizeImg(organisation.logoUrl, "90x90")} 1x`,
                            `${resizeImg(organisation.logoUrl, "180x180")} 2x`,
                            `${resizeImg(organisation.logoUrl, "270x270")} 3x`,
                          ].join(", ")}
                        />

                        <img
                          className="img-fluid"
                          src={resizeImg(organisation.logoUrl, "120x90")}
                          alt={`Logo for ${organisation.name}`}
                        />
                      </picture>
                    </div>
                  )}
                </header>
                <div className="row">
                  <div
                    css={`
                      order: 2;

                      @media (min-width: ${(p: any): string =>
                          p.theme.mdBreakpoint}) {
                        order: 1;
                      }
                    `}
                    className="col-md-7"
                  >
                    <div className="mb-5">
                      <p
                        css={`
                          white-space: pre-wrap;
                        `}
                      >
                        {organisation.description}
                      </p>
                    </div>
                    {!!organisation.opportunities.length && (
                      <section>
                        <h2 className="mb-3">
                          {organisation.totalCount} volunteer{" "}
                          {organisation.totalCount === 1
                            ? "opportunity"
                            : "opportunities"}{" "}
                          found
                        </h2>

                        <ul className="list-unstyled mb-4">
                          {organisation.opportunities.map((opp) => (
                            <li key={opp.id} className="border-bottom">
                              <OrganisationDetailsOpportunityResultCard
                                name={opp.name}
                                to={(loc) => ({
                                  pathname: opp.url,
                                  state: {
                                    referrer: {
                                      ...loc,
                                      state: {
                                        referrer: referrer,
                                        children: "Back to organisation",
                                      },
                                    },
                                  },
                                })}
                                headingProps={{ className: "h2 mb-0" }}
                                locations={locationSegmentsToReactNode(
                                  opp.locations,
                                  opp.onlineRemoteVolunteers
                                )}
                                category={
                                  opp.category ? (
                                    <>
                                      <span className="sr-only">
                                        Category:{" "}
                                      </span>
                                      <VolunteerSearchLink
                                        className="font-weight-normal text-dark"
                                        to={opp.category.url}
                                        searchQuery={{
                                          category: [opp.category.value],
                                        }}
                                        searchType="opp"
                                        aria-label={`Category: ${opp.category.name}`}
                                      >
                                        {opp.category.name}
                                      </VolunteerSearchLink>
                                    </>
                                  ) : undefined
                                }
                                typesOfWork={
                                  opp.typesOfWork.length ? (
                                    <>
                                      <span className="sr-only">
                                        Types of work:{" "}
                                      </span>
                                      {opp.typesOfWork.map((type, idx) => (
                                        <React.Fragment key={idx}>
                                          {type.url ? (
                                            <VolunteerSearchLink
                                              className="font-weight-normal text-dark"
                                              to={type.url}
                                              searchQuery={{
                                                typeofwork: [type.value],
                                              }}
                                              searchType="opp"
                                            >
                                              {type.name}
                                            </VolunteerSearchLink>
                                          ) : (
                                            <span>{type.name}</span>
                                          )}
                                          {idx < opp.typesOfWork.length - 1 && (
                                            <span>, </span>
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </>
                                  ) : null
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                    {!!organisation.totalCount && (
                      <VolunteerSearchLink
                        className="link"
                        to={organisation.opportunitySearchUrl}
                        searchQuery={{
                          organisationId: `${organisation.id}`,
                        }}
                        searchType="opp"
                      >
                        See all opportunities for {organisation.name}
                      </VolunteerSearchLink>
                    )}
                  </div>
                  <aside
                    css={`
                      order: 1;

                      @media (min-width: ${(p) => p.theme.mdBreakpoint}) {
                        order: 2;
                      }
                    `}
                    className="col-md-4 ml-auto"
                  >
                    <SidebarBox
                      css={`
                        border-color: transparent;
                        margin-top: 0;
                        padding-top: 0;
                      `}
                    >
                      <ul
                        css={`
                          > li {
                            margin-bottom: 0.5rem;
                          }
                        `}
                        className="list-unstyled"
                      >
                        {!!organisation.primaryAddress && (
                          <li>
                            <OppListSvgSprite
                              id="icons:marker"
                              width={16}
                              height={16}
                              aria-label="Location: "
                            />
                            {organisation.primaryAddress.suburb}
                            {", "}
                            {organisation.primaryAddress.state}
                          </li>
                        )}

                        <li>
                          <OppListSvgSprite
                            id="icons:ribbon"
                            width={16}
                            height={16}
                            aria-label="Category: "
                          />
                          {organisation.category}
                        </li>
                      </ul>
                    </SidebarBox>

                    <div
                      css={`
                        margin: 0 -0.5rem 1rem;
                      `}
                    >
                      <ThemeProvider
                        theme={(theme) => ({
                          ...theme,
                          dangerColor: theme.primaryColor,
                        })}
                      >
                        <FavouriteBtn
                          id={organisation.id}
                          type={FAVOURITES_ENUM.ORGANISATION}
                          onFavourite={(isFavourited) => {
                            updateUser((prev) => ({
                              ...prev,
                              favouriteCount: isFavourited
                                ? prev.favouriteCount + 1
                                : prev.favouriteCount - 1,
                            }));
                          }}
                        />
                      </ThemeProvider>
                    </div>

                    {!!organisation.website && (
                      <Block>
                        <h2 className="h4 text-reset">Contact</h2>
                        <address>
                          {!!organisation.primaryAddress && (
                            <>
                              {organisation.primaryAddress.street}
                              <br />
                              {organisation.primaryAddress.suburb},{" "}
                              {organisation.primaryAddress.state}
                              <br />
                              Australia
                            </>
                          )}
                        </address>
                        {!!organisation.website && (
                          <div className="mb-3">
                            <a href={`${organisation.website.url}`}>
                              {organisation.website.name}
                            </a>
                          </div>
                        )}
                        {!!organisation.phoneNumber && (
                          <div className="mb-3">
                            Phone {organisation.phoneNumber}
                          </div>
                        )}
                      </Block>
                    )}
                  </aside>
                </div>
              </section>
            </>
          );
        }

        return null;
      })()}
    </div>
  );
};

export default withTheme(OrganisationDetails);
